import { trackEvent } from 'user-analytics';
import { CDS_FP_ERROR_KEYS } from '../../constants/cdsFrequentPassenger';

/**
 * Analytics event names for CDS Pasajero Frecuente (Cruz del Sur loyalty program).
 */
export const CDS_FP_EVENTS = Object.freeze({
  OPT_IN_TOGGLED: 'CDS Frequent Passenger Opt-In Toggled',
  LOGIN_SUCCEEDED: 'CDS Frequent Passenger Login Succeeded',
  LOGIN_FAILED: 'CDS Frequent Passenger Login Failed',
  ENROLLMENT_COMPLETED: 'CDS Frequent Passenger Enrollment Completed',
  ACTIVATION_SUCCEEDED: 'CDS Frequent Passenger Activation Succeeded',
  LOGGED_OUT: 'CDS Frequent Passenger Logged Out',
  PASSWORD_UPDATE_SUCCEEDED: 'CDS Frequent Passenger Password Update Succeeded',
  PASSWORD_UPDATE_FAILED: 'CDS Frequent Passenger Password Update Failed',
});

/**
 * Maps a CDS Frequent Passenger error to a safe, non-PII analytics key.
 * Only known translation keys from CDS_FP_ERROR_KEYS are forwarded as-is;
 * any other message is bucketed to avoid leaking raw API/user-echoed text.
 * @param {Error|{message?: string}|undefined} error
 * @returns {string}
 */
export function resolveCdsFpAnalyticsErrorKey(error) {
  const message = error?.message;
  return message && CDS_FP_ERROR_KEYS.has(message) ? message : 'api_error';
}

/**
 * Track when a user toggles the CDS Frequent Passenger purchase opt-in checkbox.
 * @param {boolean} checked
 */
export function cdsFpOptInToggledTracker(checked) {
  trackEvent(CDS_FP_EVENTS.OPT_IN_TOGGLED, { checked });
}

/**
 * Track a successful CDS Frequent Passenger login.
 */
export function cdsFpLoginSucceededTracker() {
  trackEvent(CDS_FP_EVENTS.LOGIN_SUCCEEDED);
}

/**
 * Track a failed CDS Frequent Passenger login attempt.
 * @param {Error|{message?: string}|undefined} error
 */
export function cdsFpLoginFailedTracker(error) {
  trackEvent(CDS_FP_EVENTS.LOGIN_FAILED, { errorKey: resolveCdsFpAnalyticsErrorKey(error) });
}

/**
 * Track completion of the CDS Frequent Passenger enrollment wizard.
 */
export function cdsFpEnrollmentCompletedTracker() {
  trackEvent(CDS_FP_EVENTS.ENROLLMENT_COMPLETED);
}

/**
 * Track a successful CDS Frequent Passenger account activation.
 */
export function cdsFpActivationSucceededTracker() {
  trackEvent(CDS_FP_EVENTS.ACTIVATION_SUCCEEDED);
}

/**
 * Track a CDS Frequent Passenger logout.
 */
export function cdsFpLoggedOutTracker() {
  trackEvent(CDS_FP_EVENTS.LOGGED_OUT);
}

/**
 * Track a successful CDS Frequent Passenger password update.
 */
export function cdsFpPasswordUpdateSucceededTracker() {
  trackEvent(CDS_FP_EVENTS.PASSWORD_UPDATE_SUCCEEDED);
}

/**
 * Track a failed CDS Frequent Passenger password update.
 * The provider returns free-text 422 messages, so the error is bucketed before
 * being sent — the raw text never reaches analytics.
 * @param {Error|{message?: string}|undefined} error
 */
export function cdsFpPasswordUpdateFailedTracker(error) {
  trackEvent(CDS_FP_EVENTS.PASSWORD_UPDATE_FAILED, {
    errorKey: resolveCdsFpAnalyticsErrorKey(error),
  });
}
